import type { ClientId } from '@seaber/common/lib/clientId';
import type { EstimateTimeline } from '@seaber/common/core/scenario';
import { getTimelineTimedatas, type EventTimeline } from '../../core/Timelines';
import type { SwapDirection } from '../../core/Core';
import { getEventResourceId } from '../../core/Core';
import type { EventOrLeg, Events, TimeData } from '../../core/TimeData';
import {
  getEventLocationId,
  isCargoEventTimeData,
  isCoaEventTimeData,
  isLeg,
  isNotTimeData,
  isOffHireEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallEventTimeData,
  isSingleEventTimeData,
  isSpotEventTimeData,
  isStaticEventTimeData,
  isTCOrderEventTimeData,
  isTimeData,
  type PortCallEvent,
  PortCallEventType,
} from '../../core/TimeData';
import type {
  TimelineGroup,
  TimelineGroupBase,
  TimelineItem,
} from '../react-calendar-timeline';
import type { ScenarioTimelineGroupFields, ScheduleItem } from '../types';
import {
  ItemType,
  type TimelineEvent,
  type TimelineOffHireEvent,
  type TimelineOpenPosition,
  type TimelineTcInEvent,
  type TimelineVesselReservation,
  type TimelineVoyage,
} from '../types';
import type {
  ScenarioTimelineDiff,
  TimelineDiffState,
} from '../../ScenarioUtil';
import { mergeTimeDataDiffs } from '../../ScenarioUtil';
import type {
  LocationId,
  OpenPositionsFragment,
  RegionId,
  ScenarioTimeline_OrganizationFragment,
  VesselReservationFragment,
} from '@seaber/common/graphql/types';
import { createFragmentReconstructors } from '../estimator/estimateFragments';
import type { ScenarioType } from '../ScenarioMergeTimeline';
import type { ScenarioTimelineProps } from '../ScenarioTimeline';
import type { GetIncomeForLoadedAmount } from '../../shipment/income/income';
import type { PnlData } from '../../hooks/usePnlData';
import type { Distances } from '../../distances/context';
import type {
  ScenarioLocation,
  ScenarioOrder,
} from '../../../lib/planningData';
import type { CostSharingDetailsByPortCall } from '../../shipment/TCE';
import {
  computeTceForTimeData,
  getCostSharingDetailsForPortCalls,
} from '../../shipment/TCE';
import { getOverlappingEvents } from '../../core/util';
import {
  assertDefined,
  assertedDefined,
  exhaustiveCheck,
  sortBy,
} from '../../../lib/util';
import uniq from 'lodash/uniq';

import { getVesselCapacities } from '../../core/Vessel';
import { computeVoyagePerformanceMetrics } from '../../core/Core.voyage-metrics';
import { compact } from 'lodash';
import groupBy from 'lodash/groupBy';
import {
  freeEventFilter,
  getRelatedOrder,
  groupOrdersByPortCallId,
  isLegOrWait,
} from './utils';
import type { PnlByEstimate, PnlByShipmentId } from '../../pnl/ui/context';
import type { VesonBunker } from '../../integrations/VesonRob';
import { DefaultMap } from '@seaber/common/lib/collections';

function getPortCallEventsByPortCall(events: TimeData[]) {
  const portCallEventsByPortCallId = new Map<
    ClientId,
    Record<PortCallEventType, TimeData<PortCallEvent>>
  >();

  events.forEach((td) => {
    if (!isPortCallEventTimeData(td) || !td.event.portCallId) return;

    portCallEventsByPortCallId.set(
      td.event.portCallId,
      Object.assign(
        {
          [PortCallEventType.Arrival]: null,
          [PortCallEventType.Berthing]: null,
          [PortCallEventType.Departure]: null,
        },
        portCallEventsByPortCallId.get(td.event.portCallId),
        {
          [td.event.event]: td,
        },
      ),
    );
  });

  return portCallEventsByPortCallId;
}

/**
 * Assigns non-overlapping free events to the next shipment they precede.
 * Returns a map of shipment index -> array of events that should be included in that shipment.
 *
 * Logic:
 * - Events that overlap with shipments are handled by existing getOverlappingEvents logic
 * - Events that don't overlap with ANY shipment should be assigned to the NEXT shipment
 * - OffHire events are excluded and remain standalone
 */
export function assignNonOverlappingEventsToNextShipment(
  sortedShipments: Array<{
    start: number;
    end: number;
    portCallIds: Set<ClientId>;
  }>,
  freeEvents: EventOrLeg[],
): Map<number, EventOrLeg[]> {
  function addEventToShipment(shipmentIndex: number, event: EventOrLeg) {
    eventsByShipmentIndex.get(shipmentIndex).push(event);
  }

  const eventsByShipmentIndex = new DefaultMap<number, EventOrLeg[]>(() => []);

  if (sortedShipments.length === 0) {
    return eventsByShipmentIndex;
  }

  const assignedPortCallIds = new Set<ClientId>();
  sortedShipments.forEach(({ portCallIds }) => {
    portCallIds.forEach((portCallId) => {
      assignedPortCallIds.add(portCallId);
    });
  });

  // Collect keys of events that overlap with any shipment by time range.
  // These are handled by getOverlappingEvents instead.
  const overlappingEventKeys = new Set<string>();
  for (const event of freeEvents) {
    for (const shipment of sortedShipments) {
      if (event.start < shipment.end && event.end > shipment.start) {
        overlappingEventKeys.add(event.key);
        break;
      }
    }
  }

  // Filter out OffHire events upfront and sort by end time for efficient processing
  const sortedFreeEvents = freeEvents
    .filter((event) => {
      if (isTimeData(event) && isOffHireEventTimeData(event)) {
        return false;
      }
      if (
        event.event.portCallId &&
        assignedPortCallIds.has(event.event.portCallId)
      ) {
        return false;
      }
      if (overlappingEventKeys.has(event.key)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.end - b.end);

  let currentShipmentIndex = 0;
  for (const freeEvent of sortedFreeEvents) {
    let currentShipment = sortedShipments[currentShipmentIndex];

    if (!currentShipment) {
      break;
    }

    const portCallId = freeEvent.event.portCallId;
    if (!portCallId) {
      continue;
    }

    const isPortCallIncludedInShipment =
      currentShipment.portCallIds.has(portCallId);

    if (isPortCallIncludedInShipment) {
      continue;
    }

    while (currentShipment && currentShipment.start < freeEvent.end) {
      currentShipmentIndex++;
      currentShipment = sortedShipments[currentShipmentIndex];
    }

    if (!currentShipment) {
      break;
    }

    if (currentShipment.start >= freeEvent.end) {
      addEventToShipment(currentShipmentIndex, freeEvent);
    }
  }

  return eventsByShipmentIndex;
}

export function buildAssignedItems({
  onToggleShipment,
  expandedShipmentIds,
  onSwapScheduleEntry,
  onSwapPortCall,
  sharedOrderIds,
  timelines,
  groups,
  timelineDiff,
  estimateDiffs,
  organizations,
  scenarioType,
  isCompressedView,
  getIncomeForLoadedAmount,
  pnlData,
  scenarioName,
  distances,
  charteringOpenPositions,
  vesselReservations,
  estimateTimelinesById,
  locationsById,
  regionsById,
  euEtsCostEnabled,
  fuelEuCostEnabled,
  orderById,
  pnlByShipmentId,
  isItemActuallySelected,
  pnlByEstimate,
  vesonBunkerData,
}: {
  isItemActuallySelected: (
    estimateIdOrScenario: ClientId | 'scenario',
    orders: ScenarioOrder[] | undefined,
    id: string,
  ) => boolean;
  onToggleShipment?: (
    shipmentId: number | ClientId,
    isCollapsed: boolean,
  ) => void;
  ctrlKeyState?: boolean;
  expandedShipmentIds: Set<number | ClientId>;
  onSwapScheduleEntry?: (
    timelineId: EventTimeline['id'],
    indexToMove: number,
    direction: SwapDirection,
    timeDataItem: TimeData,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  onSwapPortCall?: (
    timelineId: EventTimeline['id'],
    portCallId: ClientId,
    shipmentIds: ClientId[],
    direction: SwapDirection,
    step: number,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => void;
  sharedOrderIds: Set<number>;
  timelines: EventTimeline[];
  groups: TimelineGroup<ScenarioTimelineGroupFields>[];
  timelineDiff: ScenarioTimelineDiff | null;
  estimateDiffs?: Map<ClientId, ScenarioTimelineDiff> | undefined;
  organizations: Map<number, ScenarioTimeline_OrganizationFragment>;
  scenarioType?: ScenarioType;
  isCompressedView?: ScenarioTimelineProps['isCompressedView'];
  getIncomeForLoadedAmount?: GetIncomeForLoadedAmount;
  pnlData?: PnlData | null;
  scenarioName?: string | null;
  distances: Distances;
  charteringOpenPositions?: OpenPositionsFragment[];
  vesselReservations?: VesselReservationFragment[];
  estimateTimelinesById?: Map<ClientId, EstimateTimeline>;
  locationsById?: Map<LocationId, ScenarioLocation>;
  regionsById?: Map<RegionId, { id: RegionId; name: string }>;
  euEtsCostEnabled?: boolean;
  fuelEuCostEnabled?: boolean;
  orderById: Map<number, ScenarioOrder> | null;
  pnlByShipmentId: PnlByShipmentId | undefined;
  pnlByEstimate: PnlByEstimate | undefined;
  vesonBunkerData?: VesonBunker;
}) {
  const itemsByGroup: Map<TimelineGroupBase['id'], ScheduleItem[]> = new Map();
  const allItems: ScheduleItem[] = [];

  function addItemToGroup(
    groupId: TimelineGroupBase['id'],
    item: ScheduleItem,
  ) {
    const items = itemsByGroup.get(groupId) ?? [];
    allItems.push(item);
    items.push(item);
    if (items.length === 1) {
      itemsByGroup.set(groupId, items);
    }
  }

  const buildTimelineItem = <
    EventType extends Events & { order: ScenarioOrder },
    IT extends ItemType.COA | ItemType.SPOT | ItemType.SCHEDULED_ORDER,
  >(
    itemType: IT,
    shipmentTds: TimeData<EventType>[],
    id: string,
    isWholeShipment: boolean,
    timelineFreeEvents: EventOrLeg[],
    group: TimelineGroup<ScenarioTimelineGroupFields>,
    organizations: ScenarioTimelineProps['organizations'],
    batchedFreeEvents: Set<EventOrLeg> | undefined,
    portCallCostSharingDetails: CostSharingDetailsByPortCall,
    isCompressedView: ScenarioTimelineProps['isCompressedView'],
    isFirstShipmentEvent: boolean,
    orderById: Map<number, ScenarioOrder> | null,
    additionalEventsForShipment: EventOrLeg[] = [],
  ): TimelineItem<TimelineVoyage> => {
    const overlappingEvents = getOverlappingEvents(
      shipmentTds,
      timelineFreeEvents,
      true,
    );

    // Combine overlapping events with non-overlapping events assigned to this shipment
    const allEventsForShipment = [
      ...overlappingEvents,
      ...additionalEventsForShipment,
    ];

    if (batchedFreeEvents && isWholeShipment) {
      allEventsForShipment.forEach((e) => batchedFreeEvents.add(e));
    }

    const orderEventsWithGlobals = sortBy(
      [...shipmentTds, ...allEventsForShipment],
      (x) => x.start,
    );
    const includedOrders = uniq(shipmentTds.map((td) => td.event.order));
    const shipmentId = includedOrders[0].shipmentId;

    const groupId = group.id;
    const prevItem = (itemsByGroup.get(groupId) ?? []).at(-1);

    const filteredTimeDataEvents = orderEventsWithGlobals.filter(isTimeData);
    const filteredOrderEventsGlobals =
      filteredTimeDataEvents.filter(isCargoEventTimeData) || [];

    const prevItemEvents = (
      prevItem && 'events' in prevItem ? prevItem.events : []
    ).filter(isCargoEventTimeData);

    const prevItemLocation = getEventLocationId(
      prevItemEvents[prevItemEvents.length - 1]?.event || {},
    );
    const currentItemLocation = getEventLocationId(
      filteredOrderEventsGlobals[0]?.event || {},
    );
    const estimateIdOrScenario = group.timeline.estimateIdOrScenario;

    const shipmentPnl =
      estimateIdOrScenario !== 'scenario'
        ? shipmentId
          ? pnlByEstimate
              ?.get(estimateIdOrScenario)
              ?.pnlByShipmentId.get(shipmentId)
          : undefined
        : shipmentId
          ? pnlByShipmentId?.get(shipmentId)
          : undefined;

    const tceData =
      getIncomeForLoadedAmount &&
      pnlData &&
      isWholeShipment &&
      shipmentId &&
      shipmentPnl
        ? computeTceForTimeData(
            filteredTimeDataEvents,
            shipmentId,
            getIncomeForLoadedAmount,
            distances,
            {
              euEtsCostEnabled: euEtsCostEnabled ?? false,
              fuelEuCostEnabled: fuelEuCostEnabled ?? false,
              outgoingCoaId: group.timeline.coaId ?? null,
              pnlData,
              portCallCostSharingDetails,
            },
            orderById,
            shipmentPnl,
            estimateIdOrScenario,
          )
        : undefined;

    const vessel = group.timeline.vessel;
    const capacities = vessel && getVesselCapacities(vessel);
    const metrics =
      capacities &&
      computeVoyagePerformanceMetrics(
        filteredTimeDataEvents,
        distances,
        capacities.massCapacity,
      );

    const diff =
      estimateIdOrScenario !== 'scenario'
        ? estimateDiffs?.get(estimateIdOrScenario)
        : timelineDiff;
    const orderTdDiffStates = shipmentTds.map((td): TimelineDiffState => {
      const entryState =
        diff?.entriesState.get(td.event.entry.clientId) ?? null;
      const { order } = td.event;
      const orderState = diff?.ordersState.get(order.id) ?? null;
      const shipmentState =
        (order.shipment &&
          diff?.shipmentToOrdersState.get(order.shipment.clientId)) ??
        null;

      return mergeTimeDataDiffs(entryState, orderState, shipmentState);
    });
    const portCallTdDiffStates = compact(
      overlappingEvents
        .filter(isPortCallEventTimeData)
        .map((td): TimelineDiffState | undefined => {
          return diff?.entriesState.get(td.event.entry.clientId);
        }),
    );

    const combinedDiff = mergeTimeDataDiffs(
      ...orderTdDiffStates,
      ...portCallTdDiffStates,
    );

    const legDataEvents = orderEventsWithGlobals.filter(isNotTimeData);
    const start_time =
      prevItem?.group === group.id && prevItemLocation === currentItemLocation
        ? shipmentTds[0]?.start
        : (
            orderEventsWithGlobals.find(isPortCallBerthingEventTimeData) ||
            filteredOrderEventsGlobals.find((td) => !isLeg(td)) ||
            filteredOrderEventsGlobals[0]
          )?.start;

    const actualId = scenarioType
      ? `${scenarioType}${scenarioName ?? ''}-${id}`
      : id;
    return {
      id: actualId,
      orders: includedOrders,
      type: itemType,
      isCollapsed: isWholeShipment,
      group: group.id,
      groupObj: group,
      start_time,
      end_time: Math.max(...shipmentTds.map((td) => td.end)),
      events: filteredTimeDataEvents,
      implicitEvents: legDataEvents,
      onToggleShipment:
        shipmentId === null || !onToggleShipment
          ? undefined
          : (isCollapsed) => onToggleShipment(shipmentId, isCollapsed),
      canChangeGroup:
        shipmentId === null || !expandedShipmentIds.has(shipmentId),
      isToggleExpanded: !!(shipmentId && expandedShipmentIds.has(shipmentId)),
      organizations,
      onSwapScheduleEntry,
      onSwapPortCall,
      isCompressedView,
      isShared: includedOrders.every((o) => sharedOrderIds.has(o.id)),
      enablePortCallUpdates: true,
      enableVoyageCharterUpdates: true,
      combinedDiff,
      scenarioType,
      tceData,
      metrics: metrics ?? undefined,
      shipmentPnl,
      isFirstShipmentEvent,
      actuallySelected: isItemActuallySelected(
        group.timeline.estimateIdOrScenario,
        includedOrders,
        actualId,
      ),
      vesonBunkerData,
    };
  };

  const batchedFreeEvents = new Set<EventOrLeg>();

  const buildItemBatches = <
    EventType extends Events & { order: ScenarioOrder },
    T extends Exclude<Events, EventType>,
  >(
    timeline: EventTimeline,
    mainEventFilter: (td: TimeData<T | EventType>) => td is TimeData<EventType>,
    group: TimelineGroup<ScenarioTimelineGroupFields>,
    generateItemId: (
      isBatch: boolean,
      firstEvent: EventType,
      shipmentId: string,
    ) => string,
    organizations: Map<number, ScenarioTimeline_OrganizationFragment>,
    isCompressedView: ScenarioTimelineProps['isCompressedView'],
    orderById: Map<number, ScenarioOrder> | null,
  ) => {
    assertDefined(group, `${timeline.type} group should be defined`);
    for (const voyageGroup of timeline.voyageGroups) {
      const mainEvents = voyageGroup.events.filter(mainEventFilter);
      const ordersByPortCallId = groupOrdersByPortCallId(mainEvents);
      const portCallCostSharingDetails = getCostSharingDetailsForPortCalls(
        voyageGroup.events,
      );

      const orderTdsByShipment = groupBy(mainEvents, (e) => {
        const order = getRelatedOrder(ordersByPortCallId, e.event);
        if (!order) {
          console.warn(
            `There is an event without an associated order and shipment so it cannot be grouped into any batch:`,
            e,
          );
        }
        return order?.shipmentId ?? '';
      });

      /** Events which will be included into all overlapping items on the timeline */
      const timelineFreeEvents: EventOrLeg[] = [
        ...voyageGroup.events.filter(freeEventFilter),
        ...voyageGroup.eventsAndLegs.filter(isLegOrWait),
      ];

      const sortedOrderTdsByShipment = sortBy(
        Object.values(orderTdsByShipment),
        (x) => x[0].start,
      );

      // Assign non-overlapping events to their respective next shipments
      const nonOverlappingEventsByShipment =
        assignNonOverlappingEventsToNextShipment(
          sortedOrderTdsByShipment.map((shipment) => {
            const portCallIds: Set<ClientId> = new Set();
            shipment.forEach(({ event }) => {
              if (event.portCallId) {
                portCallIds.add(event.portCallId);
              }
            });
            return {
              start: shipment[0].start,
              end: shipment[shipment.length - 1].end,
              portCallIds,
            };
          }),
          timelineFreeEvents,
        );

      for (const [
        shipmentIndex,
        shipmentTds,
      ] of sortedOrderTdsByShipment.entries()) {
        const firstTd = assertedDefined(
          shipmentTds[0],
          'expected at least one td in group',
        );
        const shipmentId = firstTd.event.order.shipmentId ?? null;
        const renderIndividualOrders =
          shipmentId === null || expandedShipmentIds.has(shipmentId);

        const itemType =
          timeline.type === 'tc'
            ? ItemType.SCHEDULED_ORDER
            : timeline.type === 'coa'
              ? ItemType.COA
              : timeline.type === 'spot'
                ? ItemType.SPOT
                : null;

        assertDefined(itemType, 'itemtype');

        // Get non-overlapping events assigned to this shipment
        const additionalEvents =
          nonOverlappingEventsByShipment.get(shipmentIndex) ?? [];

        if (renderIndividualOrders) {
          // We need to render each order as a separate item on the timeline when the shipment is collapsed.
          const groupedTimeDataByOrder = shipmentTds.reduce<
            TimeData<EventType>[][]
          >((acc, td) => {
            const order = getRelatedOrder(ordersByPortCallId, td.event);
            const orderId = order?.id ?? '';
            const existingOrder = acc.find(
              (arr) =>
                isCargoEventTimeData(arr[0]) &&
                arr[0].event.order?.id === orderId,
            );
            if (!existingOrder) {
              acc.push([td]);
            } else {
              existingOrder.push(td);
            }
            return acc;
          }, []);

          const groupedByVoyageCharter = groupBy(
            groupedTimeDataByOrder,
            (tds) =>
              tds[0].event.order.incomingVoyageCharterRelation?.voyageCharterId,
          );

          const isSameTds = (
            tds1: TimeData<EventType>[] | undefined,
            tds2: TimeData<EventType>[] | undefined,
          ) => {
            if (!tds1 || !tds2) return false;
            if (tds1.length !== tds2.length) return false;
            return tds1.every(
              (item, index) => item.index === tds2[index].index,
            );
          };

          for (const [index, shipmentTds] of groupedTimeDataByOrder.entries()) {
            const firstEvent = shipmentTds[0].event;
            const voyageCharterId =
              firstEvent.order.incomingVoyageCharterRelation?.voyageCharterId;

            const shipmentsByVC = voyageCharterId
              ? groupedByVoyageCharter[voyageCharterId]
              : undefined;

            const isFirstShipmentEvent = shipmentsByVC
              ? isSameTds(shipmentsByVC.at(0), shipmentTds)
              : index === 0;

            const timelineItem: TimelineItem<TimelineVoyage> =
              buildTimelineItem(
                itemType,
                shipmentTds,
                generateItemId(false, firstEvent, shipmentId?.toString() ?? ''),
                false,
                timelineFreeEvents,
                group,
                organizations,
                undefined,
                portCallCostSharingDetails,
                isCompressedView,
                isFirstShipmentEvent,
                orderById,
                additionalEvents,
              );

            addItemToGroup(group.id, timelineItem);
          }
        } else {
          const firstEvent = shipmentTds[0].event;
          const timelineItem: TimelineItem<TimelineVoyage> = buildTimelineItem(
            itemType,
            shipmentTds,
            generateItemId(true, firstEvent, shipmentId?.toString() ?? ''),
            true,
            timelineFreeEvents,
            group,
            organizations,
            batchedFreeEvents,
            portCallCostSharingDetails,
            isCompressedView,
            true, // isFirstShipmentEvent
            orderById,
            additionalEvents,
          );

          addItemToGroup(group.id, timelineItem);
        }
      }
    }
  };

  for (const timeline of timelines) {
    if (timeline.type === 'spot') {
      const groupObj = groups.find(
        (g) =>
          g.timeline.type === 'spot' &&
          g.timeline.ownerOrganizationId === timeline.ownerOrganizationId &&
          g.timeline.vesselId === timeline.vesselId,
      );
      assertDefined(groupObj, 'spot group');

      buildItemBatches(
        timeline,
        isSpotEventTimeData,
        groupObj,
        (isBatch, firstEvent, shipmentId) =>
          timeline.id +
          (isBatch
            ? `-shipment-${shipmentId}`
            : `-order-${firstEvent.order.id}`),
        organizations,
        isCompressedView,
        orderById,
      );
    } else if (timeline.type === 'coa') {
      const groupObj = groups.find(
        (g) =>
          g.timeline.type === 'coa' &&
          g.timeline.coaId === timeline.coaId &&
          g.timeline.vesselId === timeline.vesselId,
      );

      assertDefined(groupObj, 'coa group');
      buildItemBatches(
        timeline,
        isCoaEventTimeData,
        groupObj,
        (isBatch, firstEvent, shipmentId) =>
          timeline.id +
          (isBatch
            ? `-shipment-${shipmentId}`
            : `-order-${firstEvent.order.id}`),
        organizations,
        isCompressedView,
        orderById,
      );
    } else if (timeline.type === 'tc') {
      const estimateIdOrScenario = timeline.estimateIdOrScenario;
      const idSuffix =
        estimateIdOrScenario !== 'scenario' ? `-${estimateIdOrScenario}` : '';
      const groupObj =
        groups.find(
          (g) => g.timeline.type === 'tc' && g.timeline === timeline,
        ) ??
        groups.find(
          (g) =>
            g.timeline.type === 'tc' &&
            g.timeline.vesselId === timeline.vesselId &&
            g.timeline.estimateIdOrScenario === estimateIdOrScenario,
        );

      assertDefined(groupObj, 'tc group');
      buildItemBatches(
        timeline,
        isTCOrderEventTimeData,
        groupObj,
        (isBatch, firstEvent, shipmentId) =>
          timeline.id +
          (isBatch
            ? `-shipment-${shipmentId}`
            : `-${firstEvent.category}-${getEventResourceId(firstEvent)}`),
        organizations,
        isCompressedView,
        orderById,
      );

      const events = getTimelineTimedatas(timeline);
      const portCallEventsByPortCall = getPortCallEventsByPortCall(events);
      const renderedSingleEvents: Set<string> = new Set();

      for (const td of events) {
        if (isOffHireEventTimeData(td)) {
          // TODO: Extract timeline item construction to function for readability
          const {
            entry: { clientId },
            locationAName,
            locationBName,
          } = td.event;
          const groupObj = groups.find(
            (g) =>
              g.timeline.type === 'tc' &&
              g.timeline.vesselId === td.event.vesselId &&
              g.timeline.estimateIdOrScenario === estimateIdOrScenario,
          );
          assertDefined(groupObj, 'group for offhire');
          const actualId =
            (scenarioType
              ? `${scenarioType}-offhire-${clientId}`
              : `offhire-${clientId}`) + idSuffix;
          const ti: TimelineItem<TimelineOffHireEvent> = {
            timeData: td,
            locationAName,
            locationBName,
            id: actualId,
            vessel: td.event.vessel,
            type: ItemType.OFFHIRE,
            group: groupObj.id,
            groupObj,
            start_time: td.start,
            end_time: td.end,
            actuallySelected: isItemActuallySelected(
              groupObj.timeline.estimateIdOrScenario,
              undefined,
              actualId,
            ),
          };

          addItemToGroup(groupObj.id, ti);

          continue;
        }

        if (isSingleEventTimeData(td)) {
          const {
            entry: { clientId },
            locationName,
            vessel,
            event,
            portCallId,
          } = td.event;
          if (batchedFreeEvents.has(td)) {
            continue;
          }

          const portCallEvents = portCallId
            ? portCallEventsByPortCall.get(portCallId)
            : undefined;

          const groupObj = groups.find(
            (g) =>
              g.timeline.type === 'tc' &&
              g.timeline.vesselId === td.event.vesselId &&
              g.timeline.estimateIdOrScenario === estimateIdOrScenario,
          );
          assertDefined(groupObj, 'group for single event');
          const start_time = portCallEvents?.Berth?.start ?? td.start;
          const end_time = portCallEvents?.Depart?.end ?? td.end;

          // avoid render duplicate single events by unique key

          const key =
            `${event}-${start_time}-${end_time}-${locationName}-${vessel.id}` +
            idSuffix;
          if (renderedSingleEvents.has(key)) {
            continue;
          }

          const actualId =
            (scenarioType
              ? `${scenarioType}-single-${clientId}`
              : `single-${clientId}`) + idSuffix;
          const ti: TimelineItem<TimelineEvent> = {
            timeData: td,
            locationName,
            id: actualId,
            vessel,
            type: ItemType.EVENT,
            eventType: event,
            group: groupObj.id,
            groupObj,
            start_time,
            end_time,
            portCallEvents,
            actuallySelected: isItemActuallySelected(
              groupObj.timeline.estimateIdOrScenario,
              undefined,
              actualId,
            ),
          };

          addItemToGroup(groupObj.id, ti);
          renderedSingleEvents.add(key);

          continue;
        }

        if (isStaticEventTimeData(td) && td.event.staticEventType === 'TC_IN') {
          const actualId =
            (scenarioType
              ? `${scenarioType}-tc-in-${td.event.entry.clientId}`
              : `tc-in-${td.event.entry.clientId}`) + idSuffix;
          const timelineItem: TimelineItem<TimelineTcInEvent> = {
            end_time: td.end,
            group: groupObj.id,
            groupObj,
            id: actualId,
            start_time: td.start,
            timeData: td,
            type: ItemType.TC_IN,
            actuallySelected: isItemActuallySelected(
              groupObj.timeline.estimateIdOrScenario,
              undefined,
              actualId,
            ),
          };

          addItemToGroup(groupObj.id, timelineItem);

          continue;
        }
      }
    } else {
      exhaustiveCheck(
        timeline,
        `Unexpected timeline type ${(timeline as EventTimeline).type}`,
      );
    }
  }

  // Create helpers to reconstruct full fragments from estimate simplified data
  const vesselReservationsById = new Map(
    (vesselReservations ?? []).map((vr) => [vr.id, vr]),
  );
  const { toOpenPositionsFragment, toVesselReservationFragment } =
    createFragmentReconstructors({
      locationsById,
      regionsById,
      vesselReservationsById,
    });

  // Helper to add open position item to a group
  const addOpenPositionItem = (
    op: OpenPositionsFragment,
    groupObj: TimelineGroup<ScenarioTimelineGroupFields>,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => {
    const start = new Date(op.startTime);
    const end_time = new Date(start).setDate(start.getDate() + 5);
    const idSuffix =
      estimateIdOrScenario !== 'scenario' ? `-${estimateIdOrScenario}` : '';
    const actualId = `open_position_${op.id}${idSuffix}`;
    const ti: TimelineItem<TimelineOpenPosition> = {
      type: ItemType.OPEN,
      id: actualId,
      start_time: start.getTime(),
      group: groupObj.id,
      openPosition: op,
      groupObj,
      end_time,
      actuallySelected: isItemActuallySelected(
        estimateIdOrScenario,
        undefined,
        actualId,
      ),
    };
    addItemToGroup(groupObj.id, ti);
  };

  // Helper to add vessel reservation item to a group
  const addReservationItem = (
    vr: VesselReservationFragment,
    groupObj: TimelineGroup<ScenarioTimelineGroupFields>,
    estimateIdOrScenario: ClientId | 'scenario',
  ) => {
    const start = new Date(vr.startTime);
    const start_time = start.getTime();
    const end_time = new Date(start).setDate(start.getDate() + vr.duration);
    const idSuffix =
      estimateIdOrScenario !== 'scenario' ? `-${estimateIdOrScenario}` : '';
    const actualId = `vessel_reservation_${vr.id}${idSuffix}`;
    const ti: TimelineItem<TimelineVesselReservation> = {
      type: ItemType.RESERVATION,
      id: actualId,
      group: groupObj.id,
      vesselReservation: vr,
      isCompressedView,
      groupObj,
      start_time,
      end_time,
      actuallySelected: isItemActuallySelected(
        estimateIdOrScenario,
        undefined,
        actualId,
      ),
    };
    addItemToGroup(groupObj.id, ti);
  };

  // Add open positions and reservations for each group
  for (const groupObj of groups) {
    if (groupObj.timeline.type !== 'tc') continue;

    const { vesselId, estimateIdOrScenario } = groupObj.timeline;

    if (estimateIdOrScenario === 'scenario') {
      // Main scenario timeline: use database open positions and reservations
      for (const op of charteringOpenPositions ?? []) {
        if (op.vesselId === vesselId) {
          addOpenPositionItem(op, groupObj, 'scenario');
        }
      }
      for (const vr of vesselReservations ?? []) {
        if (vr.vesselId === vesselId) {
          addReservationItem(vr, groupObj, 'scenario');
        }
      }
    } else {
      // Estimate timeline: reconstruct full fragments from simplified stored data
      const estimate = estimateTimelinesById?.get(estimateIdOrScenario);
      if (estimate) {
        for (const op of estimate.openPositions ?? []) {
          if (op.vesselId === vesselId) {
            addOpenPositionItem(
              toOpenPositionsFragment(op),
              groupObj,
              estimateIdOrScenario,
            );
          }
        }
        for (const vr of estimate.vesselReservations ?? []) {
          if (vr.vesselId === vesselId) {
            addReservationItem(
              toVesselReservationFragment(vr),
              groupObj,
              estimateIdOrScenario,
            );
          }
        }
      }
    }
  }

  // Find last non TC In event on the timeline and mark it as the last one
  // Note: groupItems is not sorted
  itemsByGroup.forEach((groupItems) => {
    let lastNonTcInItem: ScheduleItem | undefined;
    groupItems.forEach((item) => {
      if (
        item &&
        item.type !== ItemType.TC_IN &&
        (!lastNonTcInItem || item.start_time > lastNonTcInItem.start_time)
      ) {
        lastNonTcInItem = item;
      }
    });

    if (lastNonTcInItem) {
      lastNonTcInItem.isLastScheduleItem = true;
    }
  });

  return allItems;
}
